<template>
  <div>
    <div class="messageContainer" @mouseover="isHovered = true" @mouseleave="isHovered = false">
      <div v-if="message.mainContent" class="message" style="white-space: pre-line" :style="elementStyle">
        <div v-if="areDetailsShown" v-show="isHovered" class="logContainer" :style="elementStyle">
          <Logger
            :intent-analysed="intentAnalysed"
            :action-deduced="actionDeduced"
            :context="context"
            :intent-info="config"
            :is-hovered="isHovered"
          />
        </div>
        <!--eslint-disable vue/no-v-html-->
        <p v-html="formatMessage(message.mainContent)" />
        <!--eslint-enable vue/no-v-html-->
      </div>
      <div v-if="message.images && message.images.length" class="messageImg">
        <div v-if="!message.mainContent && areDetailsShown" v-show="isHovered" class="logContainer">
          <Logger
            :intent-analysed="intentAnalysed"
            :action-deduced="actionDeduced"
            :context="context"
            :intent-info="config"
            :is-hovered="isHovered"
          />
        </div>
        <img
          v-for="(imageSrc, index) in messageImages"
          :key="index"
          v-lazy="imageSrc"
          @click.prevent="openSlide(index)"
          @load="scrollToBottom(message.isNewMessage)"
        />
        <div class="img-zoom-icon" @click.prevent="openSlide()">
          <i class="fa fa-search"></i>
        </div>
      </div>
      <div v-if="message.videos && message.videos.length" class="messageVideo">
        <div class="videoContainer">
          <div v-if="message.videos[0].cover && showCover" @click="playVideo">
            <i class="fas fa-play-circle" aria-hidden="true"></i>
            <img
              v-lazy="message.videos[0].cover"
              :src="message.videos[0].cover"
              @load="scrollToBottom(message.isNewMessage)"
            />
          </div>
          <video
            v-show="!showCover || !message.videos[0].cover"
            :id="videoId"
            :poster="message.videos[0].cover"
            controls
            @playing="updatePaused"
          >
            <source v-for="(source, key) in message.videos[0].urls" :key="key" :src="source" />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p v-html="$t('video_unsupported_message', { videoSource: message.videos[0].urls[0] })"></p>
          </video>
        </div>
      </div>
      <LoggerLinks
        :intent-analysed="intentAnalysed"
        :action-deduced="actionDeduced"
        :are-details-shown="areDetailsShown"
        :assistant-id="assistantId"
        :assistant-slug="assistantSlug"
        @openUrl="openUrl"
      />
      <div v-show="isHovered" class="messageTime">{{ formatDate(message.sentTime, locale, $t) }}</div>
    </div>
  </div>
</template>

<script>
// Components
import Logger from './Logger'
import LoggerLinks from './LoggerLinks'

// Config
import config from '../../config/messages'
import defaultConfig from '../../config'

// Dependencies
import { mapGetters } from 'vuex'

// utils
import { mixin, formatDate, areDetailsShown, scrollToBottom } from '../../lib/utils'
import audioLib from '../../lib/audio'
import videoLib from '../../lib/video'

export default {
  name: 'Message',
  components: {
    Logger,
    LoggerLinks
  },
  mixins: [mixin],
  props: {
    message: { type: Object, required: true },
    intentAnalysed: { type: Object, default: null },
    context: { type: Object, default: null },
    actionDeduced: { type: Object, default: null },
    isShowLogs: { type: Boolean, default: false },
    elementStyle: { type: [Object, Boolean], default: null },
    assistantId: { type: String, required: true },
    assistantSlug: { type: String, required: true }
  },
  data: function() {
    return { config, isHovered: false, showCover: true }
  },
  computed: {
    ...mapGetters({
      isTTSEnabled: 'isTTSEnabled',
      locale: 'locale'
    }),
    areDetailsShown: function() {
      return areDetailsShown(this.context, this.intentAnalysed, this.actionDeduced, this.isShowLogs)
    },
    videoId() {
      return `video_${this.message.sentTime}`
    },
    messageImages() {
      return this.message.imagesObj ? this.message.imagesObj.map(obj => obj.resizedImage) : this.message.image
    }
  },
  methods: {
    formatDate,
    scrollToBottom,
    openUrl(url) {
      this.$emit('openUrl', url)
    },
    // Play the video triggered from click event on overlay icon
    playVideo() {
      this.showCover = false
      const video = document.getElementById(`video_${this.message.sentTime}`)
      if (video) {
        video.play()
      }
    },
    // Pause current playing audio/video when starting to play a video
    // event triggered on 'playing' event
    updatePaused(event) {
      // Stop audio
      audioLib.stop(this.isTTSEnabled)
      // Stop other playing video
      videoLib.pauseVideos(event.target)
    },
    openSlide(index = 0) {
      const mediaSources = []

      // The imagesObj will present for new messages, it will not present for older conversations, in that case message.images is used
      // imagesObj contains both raw image and resized image
      if (this.message.imagesObj && this.message.imagesObj.length) {
        mediaSources.push(...this.message.imagesObj.map(obj => obj.rawImage))
      } else if (this.message.images && this.message.images.length) {
        mediaSources.push(...this.message.images)
      }

      // @TODO: Add message videos after the issue get closed https://github.com/sachinchoolur/lg-video.js/issues/9
      // see https://trello.com/c/qcE39zcN/1048-webchat-video-lightbox

      if (!mediaSources.length) {
        return
      }

      parent.postMessage(
        {
          event: defaultConfig.FRAME_EVENT_TYPES.SENT.OPEN_LIGHT_GALLERY,
          index,
          mediaSources
        },
        '*'
      )
    }
  }
}
</script>
